import React, { FormEvent, useEffect, useState } from 'react'
import { useHistory, withRouter } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import { FullScreenPage } from '@acorns/web-components'
import { Button } from '@acorns/web-components'
import { TextareaInput } from '@acorns/web-components'
import { datadogRum } from '@datadog/browser-rum'
import styled from 'styled-components'

import DocumentUpload from 'components/DocumentUpload'
import ProgressModal from 'components/ProgressModal'
import { Heading } from 'components/typography'
import {
  DocumentType,
  useUnauthenticatedCreateUploadUrlMutation,
  useUnauthenticatedSaveUploadMutation,
  useValidateSuspendedUploadTokenMutation,
} from 'generated/types'
import { useQueryParams } from 'hooks/useQueryParams'

import { theme } from '../../theme'
import { uploadDirectToS3 } from './upload-utils'

const Content = styled.div`
  padding: 50px;

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    padding: 40px;
  }

  @media screen and (max-width: ${theme('breakpoints.sm')}px) {
    padding: 20px;
  }
`

const Description = styled.div`
  color: ${theme('colors.slate')};
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  margin: 2px 0 30px;
`

const ErrorDescription = styled.div`
  color: ${theme('colors.red')};
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  margin: 2px 0 30px;
`

const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  align-items: center;
`

const UploadButton = styled(Button)`
  &&& {
    min-width: 150px;
    @media screen and (max-width: ${theme('breakpoints.sm')}px) {
      width: 100%;
    }
  }
`

const StyledTextareaInput = styled(TextareaInput)`
  &:first-child {
    &&& {
      font-weight: 600;
    }
  }
`

const Logo = styled(AcornsIcon)`
  path {
    fill: ${(props) => props.theme.colors.green};
  }
`

const FooterContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px;
  border-top: 1px solid ${(props) => props.theme.colors.stoneLight};
  opacity: 0.5;
`

const FooterText = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: ${(props) => props.theme.colors.stone};
  padding-left: 10px;
`

function AccountSuspendedUploadPage(props: any) {
  const [files, setFiles] = useState<File[]>([])
  const [uploadCompleted, setUploadCompleted] = useState(false)
  const [showProgressModal, setShowProgressModal] = useState(false)
  const [invalidToken, setInvalidToken] = useState(false)
  const [userComment, setUserComment] = useState('')
  const [failedUploadFileNames, setFailedUploadFileNames] = useState<
    Set<string>
  >(new Set())
  const [progress, setProgress] = useState(0)

  const history = useHistory()
  const uploadToken = useQueryParams(history?.location?.search, 'token')

  const [validateUploadToken] = useValidateSuspendedUploadTokenMutation()
  const [createUploadUrl] = useUnauthenticatedCreateUploadUrlMutation()
  const [saveUploadUrl] = useUnauthenticatedSaveUploadMutation({
    onCompleted: () => setProgress(1),
  })

  let useruuid = ''
  const ticketMessage: string[] = []

  const handleCommentChange = (e: FormEvent<HTMLTextAreaElement>) => {
    setUserComment((e.target as HTMLTextAreaElement).value)
  }

  const handleDoneClick = () => {
    setShowProgressModal(false)
    setUploadCompleted(false)
    props.reset()
  }

  const handleDocsChange = (newFiles: File[]) => {
    const failedUploads: Set<string> = new Set()
    newFiles.forEach((file) => {
      // if there is still a failed upload
      if (failedUploadFileNames.has(file.name)) {
        failedUploads.add(file.name)
      }
    })
    setFailedUploadFileNames(failedUploads)
    setFiles(newFiles)
  }

  const uploadFiles = async () => {
    if (files && files.length) {
      const { data: { validateUserSuspendedUploadToken } = {} } =
        await validateUploadToken({
          variables: {
            uploadToken,
          },
        })
      if (
        validateUserSuspendedUploadToken?.__typename ===
          'UserSuspendedUploadTokenValidation' &&
        validateUserSuspendedUploadToken?.tokenValid
      ) {
        setShowProgressModal(true)
        const uploadUrls = await Promise.all(
          files.map(async (doc: File) => {
            const { data: { unauthenticatedCreateUploadUrl } = {} } =
              await createUploadUrl({
                variables: {
                  token: uploadToken,
                  documentType: DocumentType.Miscellaneous,
                  fileName: doc.name,
                },
              })

            if (unauthenticatedCreateUploadUrl) {
              const { __typename, url, uploadUuid, userId, ...uploadParams } =
                unauthenticatedCreateUploadUrl
              useruuid = userId
              ticketMessage.push(useruuid)

              return {
                file: doc,
                url,
                name: doc.name,
                uploadUuid,
                uploadParams,
              }
            } else {
              return null
            }
          }),
        )

        const uploadUuids: string[] = []
        await Promise.all(
          uploadUrls.map(async (upload) => {
            if (upload) {
              const uploadResponse = await uploadDirectToS3(
                upload.url,
                upload.file,
                upload.uploadParams,
              )

              if (uploadResponse.status === 204) {
                uploadUuids.push(upload.uploadUuid)
              } else {
                setShowProgressModal(false)
                setFailedUploadFileNames((prev) =>
                  new Set(prev).add(upload.name),
                )
              }
            }
          }),
        )

        if (failedUploadFileNames.size === 0 && uploadUuids.length > 0) {
          setProgress(0.5)

          saveUploadUrl({
            variables: {
              token: uploadToken,
              uploadUuids: uploadUuids,
              additionalInfo: userComment,
            },
          })
            .then(() => {
              setUploadCompleted(true)
              setFiles([])
            })
            .catch((error) => {
              datadogRum.addError(error)
            })
        }
      } else {
        // invalid token case
        setInvalidToken(true)
      }
    }
  }

  const handleCloseModal = () => {
    setShowProgressModal(false)
  }

  useEffect(() => {
    if (props.failed) {
      setShowProgressModal(false)
    }
  }, [props.failed, setShowProgressModal, progress])

  return (
    <div>
      <FullScreenPage
        content={
          <Content>
            <Heading style={{ lineHeight: '24px' }}>
              Your account is currently locked.
            </Heading>

            <Description>
              Please upload all requested documents and answer all questions in
              the space below that are needed to unlock your account.
            </Description>

            <DocumentUpload
              isMultiple={true}
              onChange={handleDocsChange}
              failedUploadNames={failedUploadFileNames}
            />

            <StyledTextareaInput
              id="comments"
              label="Additional Information"
              name="comments"
              onChange={handleCommentChange}
              placeholder=""
              value={userComment}
              charCounter={true}
              maxlength={250}
            />

            {invalidToken && (
              <ErrorDescription>
                The link has expired. Please request a new one or contact us.
              </ErrorDescription>
            )}

            <Actions>
              <UploadButton
                disabled={!files || !files.length || invalidToken}
                onPress={uploadFiles}
                data-testid="suspended_document_upload_button"
              >
                Upload Files
              </UploadButton>
            </Actions>
            {showProgressModal ? (
              <ProgressModal
                completed={uploadCompleted}
                isOpen={showProgressModal}
                onDoneClick={handleDoneClick}
                onRequestClose={handleCloseModal}
                progress={progress}
              />
            ) : null}
            <FooterContainer>
              <Logo
                icon={AcornsIcon.Icon.AcornsLogo}
                width="24px"
                height="24px"
              />
              <FooterText>Grow your oak</FooterText>
            </FooterContainer>
          </Content>
        }
      />
    </div>
  )
}

const SuspendedUploadPage = withRouter(AccountSuspendedUploadPage)
export default SuspendedUploadPage
